import React, { useState, useEffect } from 'react'
import {
  Modal,
  Alert,
  Table,
  Checkbox,
  Button,
  Spin,
  Select,
  Row,
  Col,
  Card,
  message,
  Tag,
  InputNumber,
  Divider,
  Modal as ImageModal,
  Progress,
} from 'antd'
import {
  FireOutlined,
  ThunderboltOutlined,
  PercentageOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  GiftOutlined,
  TrophyOutlined,
  ShoppingOutlined,
  ZoomInOutlined,
  DollarOutlined,
  QuestionCircleOutlined,
  BankOutlined,
} from '@ant-design/icons'
import backendApis from '../../utils/backendApis'
import commaNumber from 'comma-number'
import SummerBlackFridayPDP from '../../images/summerBlackFriday/summerBlackFridayPDP.svg'
import { useHistory } from 'react-router-dom'

const { Option } = Select

const SummerBlackFridayBulkApplicationModal = ({
  showModal,
  setShowModal,
  dealType,
  promotionInfo,
  onSuccess,
  onClose,
  singleItem = null,
  isMainScreen = false,
}) => {
  const history = useHistory()
  const [selectedItems, setSelectedItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [discountRate, setDiscountRate] = useState(10)
  const [totalItemsCount, setTotalItemsCount] = useState(0)
  const [displayItems, setDisplayItems] = useState([])
  const [selectedItemIds, setSelectedItemIds] = useState([])
  const [isAllSelected, setIsAllSelected] = useState(true)
  const [stockNumbers, setStockNumbers] = useState({})
  const [defaultStockNumber, setDefaultStockNumber] = useState(1000)
  const [isImagePopupOpen, setIsImagePopupOpen] = useState(false)

  // 전체 상품 정보 가져오기
  const fetchAllItems = async () => {
    // 단일 상품 모드인 경우
    if (singleItem) {
      setDisplayItems([singleItem])
      setSelectedItems([singleItem])
      setSelectedItemIds([singleItem._id])
      setTotalItemsCount(1)
      setIsAllSelected(true)
      setStockNumbers({ [singleItem._id]: defaultStockNumber })
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      // 신청 가능한 전체 상품 조건
      const itemCondition = {
        soldOut: false,
        outOfStock: { $ne: null },
        $or: [
          { itemFilters: { $eq: null } },
          {
            itemFilters: {
              $not: {
                $elemMatch: {
                  $and: [{ key: 'deal', value: dealType }],
                },
              },
            },
          },
        ],
      }

      // 전체 상품 개수 가져오기
      const countResult = await backendApis.getSellerItemsCountByCondition({
        type: 'item',
        itemCondition,
      })

      let totalCount = 0
      if (countResult?.status === 200) {
        totalCount = countResult?.data || 0
        setTotalItemsCount(totalCount)
      } else {
        console.error('Failed to get items count:', countResult)
        throw new Error('상품 개수를 가져오는데 실패했습니다')
      }

      // 상품이 없는 경우 처리
      if (totalCount === 0) {
        setDisplayItems([])
        setSelectedItems([])
        setSelectedItemIds([])
        setIsAllSelected(false)
        setIsLoading(false)
        return
      }

      // 모든 상품을 배치로 가져오기 (성능 최적화)
      const batchSize = 500
      const totalPages = Math.ceil(totalCount / batchSize)
      let allItems = []

      console.log(`Fetching ${totalCount} items in ${totalPages} batches...`)

      for (let page = 1; page <= totalPages; page++) {
        try {
          const result =
            await backendApis.getSellerSegementedItemsInfoByCondition(
              {
                type: 'item',
                itemCondition,
              },
              page,
              batchSize,
            )

          console.log(`Batch ${page}/${totalPages} result:`, result?.status)

          if (result?.status === 2000) {
            const items = result?.data?.itemsInfo || []
            allItems = [...allItems, ...items]
          } else {
            console.error(`Failed to fetch batch ${page}:`, result)
          }
        } catch (batchError) {
          console.error(`Error fetching batch ${page}:`, batchError)
        }
      }

      console.log(`Total items fetched: ${allItems.length}`)

      if (allItems.length === 0) {
        throw new Error('상품 정보를 가져올 수 없습니다')
      }

      setDisplayItems(allItems)
      setSelectedItems(allItems)
      setSelectedItemIds(allItems.map((item) => item._id))
      setIsAllSelected(true)

      // 각 상품의 기본 재고량 설정
      const initialStockNumbers = {}
      allItems.forEach((item) => {
        initialStockNumbers[item._id] = defaultStockNumber
      })
      setStockNumbers(initialStockNumbers)
    } catch (error) {
      console.error('Error in fetchAllItems:', error)
      message.error(error.message || '상품 정보를 불러오는데 실패했습니다')
      setDisplayItems([])
      setSelectedItems([])
      setSelectedItemIds([])
      setIsAllSelected(false)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (showModal) {
      fetchAllItems()
    }
  }, [showModal])

  // 상품 선택 처리
  const handleItemSelect = (itemId, checked) => {
    if (checked) {
      setSelectedItemIds([...selectedItemIds, itemId])
    } else {
      setSelectedItemIds(selectedItemIds.filter((id) => id !== itemId))
      setIsAllSelected(false)
    }
  }

  // 전체 선택/해제
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItemIds(displayItems.map((item) => item._id))
      setIsAllSelected(true)
    } else {
      setSelectedItemIds([])
      setIsAllSelected(false)
    }
  }

  // 재고량 변경 핸들러
  const handleStockNumberChange = (itemId, value) => {
    setStockNumbers((prev) => ({
      ...prev,
      [itemId]: value,
    }))
  }

  // 전체 재고량 일괄 변경
  const handleBulkStockNumberChange = (value) => {
    const newStockNumbers = {}
    displayItems.forEach((item) => {
      newStockNumbers[item._id] = value
    })
    setStockNumbers(newStockNumbers)
    setDefaultStockNumber(value)
  }

  // 신청 처리
  const handleSubmit = async () => {
    if (selectedItemIds.length === 0) {
      Modal.warning({
        title: '상품 선택 필요',
        content: '최소 1개 이상의 상품을 선택해주세요.',
      })
      return
    }

    // 선택된 상품이 많은 경우 확인
    if (selectedItemIds.length > 100) {
      Modal.confirm({
        title: '대량 신청 확인',
        content: `총 ${commaNumber(
          selectedItemIds.length,
        )}개의 상품을 신청하시겠습니까? 처리에 시간이 소요될 수 있습니다.`,
        okText: '신청',
        cancelText: '취소',
        onOk: () => processSubmit(),
      })
    } else {
      processSubmit()
    }
  }

  const processSubmit = async () => {
    setIsSubmitting(true)

    try {
      const batchSize = 100 // 한 번에 처리할 상품 수
      let processedCount = 0
      let failedCount = 0

      // 진행률 표시 모달
      const progressModal = Modal.info({
        title: '프로모션 신청 중...',
        content: (
          <div>
            <Progress percent={0} />
            <p className='text-sm text-gray-600 mt-2'>
              {commaNumber(processedCount)} /{' '}
              {commaNumber(selectedItemIds.length)} 완료
            </p>
          </div>
        ),
        closable: false,
        maskClosable: false,
        okButtonProps: { style: { display: 'none' } },
      })

      // 선택된 상품들을 배치로 처리
      const selectedItems = displayItems.filter((item) =>
        selectedItemIds.includes(item._id),
      )

      for (let i = 0; i < selectedItems.length; i += batchSize) {
        const batch = selectedItems.slice(i, i + batchSize)

        try {
          const dealItemsInfo = batch.map((item) => {
            const dealOptionsInfo = processDealOptionsInfo(item)
            const dealTeamPurchasePrice = calculateDealTeamPurchasePrice(
              item,
              dealOptionsInfo,
            )

            return {
              itemId: item._id,
              itemTitle: item.itemTitle,
              dealType,
              dealOptionsInfo,
              dealTeamPurchasePrice: parseInt(dealTeamPurchasePrice, 10),
            }
          })

          const result = await backendApis.registerMultiDealOptionsInfo({
            dealItemsList: dealItemsInfo,
          })

          if (result?.status === 200) {
            processedCount += batch.length
          } else {
            failedCount += batch.length
          }

          // 진행률 업데이트
          const progress = Math.round(
            (processedCount / selectedItemIds.length) * 100,
          )
          progressModal.update({
            content: (
              <div>
                <Progress
                  percent={progress}
                  status={failedCount > 0 ? 'exception' : 'active'}
                />
                <p className='text-sm text-gray-600 mt-2'>
                  {commaNumber(processedCount)} /{' '}
                  {commaNumber(selectedItemIds.length)} 완료
                  {failedCount > 0 && (
                    <span className='text-red-500 ml-2'>
                      ({commaNumber(failedCount)} 실패)
                    </span>
                  )}
                </p>
              </div>
            ),
          })
        } catch (error) {
          console.error('Batch processing error:', error)
          failedCount += batch.length
        }
      }

      // 완료 처리
      progressModal.destroy()

      if (failedCount === 0) {
        message.success(
          `${commaNumber(processedCount)}개 상품이 성공적으로 신청되었습니다!`,
        )
        onSuccess && onSuccess()
        setShowModal(false)
      } else {
        Modal.warning({
          title: '부분 성공',
          content: `${commaNumber(
            processedCount,
          )}개 상품이 신청되었고, ${commaNumber(
            failedCount,
          )}개 상품이 실패했습니다.`,
        })
        onSuccess && onSuccess()
      }
    } catch (error) {
      Modal.error({
        title: '신청 실패',
        content: '프로모션 신청 중 오류가 발생했습니다. 다시 시도해주세요.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // 옵션 정보 처리 헬퍼 함수
  const processDealOptionsInfo = (item) => {
    const dealOptionsInfo = JSON.parse(JSON.stringify(item?.optionsInfo || {}))
    const discountFactor = 1 - discountRate / 100
    const itemStockNumber = stockNumbers[item._id] || defaultStockNumber

    if (
      dealOptionsInfo.optionPrices &&
      dealOptionsInfo.optionPrices.length > 0
    ) {
      dealOptionsInfo.optionPrices = dealOptionsInfo.optionPrices.map(
        (priceItem) => {
          if (Array.isArray(priceItem)) {
            return priceItem.map((option) => ({
              ...option,
              teamPurchasePrice: Math.floor(
                (option.teamPurchasePrice || 0) * discountFactor,
              ),
              stockNumber: itemStockNumber,
            }))
          } else {
            return {
              ...priceItem,
              teamPurchasePrice: Math.floor(
                (priceItem.teamPurchasePrice || 0) * discountFactor,
              ),
              stockNumber: itemStockNumber,
            }
          }
        },
      )
    } else {
      dealOptionsInfo.teamPurchasePrice = Math.floor(
        (item?.teamPurchasePrice || 0) * discountFactor,
      )
      dealOptionsInfo.stockNumber = itemStockNumber
    }

    return dealOptionsInfo
  }

  // 딜 가격 계산 헬퍼 함수
  const calculateDealTeamPurchasePrice = (item, dealOptionsInfo) => {
    const discountFactor = 1 - discountRate / 100
    let dealTeamPurchasePrice = 0

    if (
      dealOptionsInfo.optionPrices &&
      dealOptionsInfo.optionPrices.length > 0
    ) {
      const dealPriceArrayTemp = dealOptionsInfo.optionPrices.map(
        (optionPrice) => {
          if (Array.isArray(optionPrice) && optionPrice.length > 0) {
            const priceArray = optionPrice.map((doc) => {
              if (
                !doc.sold_out &&
                doc.stockNumber > 0 &&
                doc.teamPurchasePrice > 0
              ) {
                return doc.teamPurchasePrice
              }
              return null
            })
            return priceArray
          } else if (optionPrice && !Array.isArray(optionPrice)) {
            if (
              !optionPrice.sold_out &&
              optionPrice.stockNumber > 0 &&
              optionPrice.teamPurchasePrice > 0
            ) {
              return optionPrice.teamPurchasePrice
            }
          }
          return null
        },
      )

      const dealPriceArray = dealPriceArrayTemp
        .flat()
        .filter((price) => price !== null && price !== undefined && price > 0)

      if (dealPriceArray.length > 0) {
        dealTeamPurchasePrice = Math.min(...dealPriceArray)
      } else {
        dealTeamPurchasePrice = Math.floor(
          (item?.teamPurchasePrice || 0) * discountFactor,
        )
      }
    } else {
      dealTeamPurchasePrice = Math.floor(
        (item?.teamPurchasePrice || 0) * discountFactor,
      )
    }

    if (isNaN(dealTeamPurchasePrice) || dealTeamPurchasePrice <= 0) {
      dealTeamPurchasePrice = Math.floor(
        (item?.teamPurchasePrice || 0) * discountFactor,
      )
    }

    return dealTeamPurchasePrice
  }

  // 할인율 옵션 생성 (5% ~ 50%, 5% 단위)
  const discountOptions = []
  for (let i = 5; i <= 50; i += 5) {
    discountOptions.push(
      <Option key={i} value={i}>
        {i}%
        {i === promotionInfo.recommendedDiscountRate && (
          <Tag color='orange' className='ml-2'>
            권장
          </Tag>
        )}
        {i === promotionInfo.defaultDiscountRate && (
          <Tag color='blue' className='ml-2'>
            기본
          </Tag>
        )}
      </Option>,
    )
  }

  // 테이블 컬럼 정의
  const columns = [
    // 단일 상품 모드일 때는 체크박스 열 숨기기
    ...(!singleItem
      ? [
          {
            title: (
              <Checkbox
                checked={isAllSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            ),
            dataIndex: 'select',
            key: 'select',
            width: 40,
            render: (_, record) => (
              <Checkbox
                checked={selectedItemIds.includes(record._id)}
                onChange={(e) => handleItemSelect(record._id, e.target.checked)}
              />
            ),
          },
        ]
      : []),
    {
      title: '상품명',
      dataIndex: 'itemTitle',
      key: 'itemTitle',
      ellipsis: true,
      render: (text) => <span className='text-xs'>{text}</span>,
    },
    {
      title: '현재가',
      dataIndex: 'teamPurchasePrice',
      key: 'teamPurchasePrice',
      width: 80,
      render: (price) => (
        <span className='text-xs'>{commaNumber(price)}원</span>
      ),
    },
    {
      title: '프로모션가',
      key: 'promotionPrice',
      width: 90,
      render: (_, record) => (
        <span className='text-xs font-semibold text-red-500'>
          {commaNumber(
            Math.floor(record.teamPurchasePrice * (1 - discountRate / 100)),
          )}
          원
        </span>
      ),
    },
    {
      title: '재고',
      key: 'stockNumber',
      width: 80,
      render: (_, record) => (
        <InputNumber
          min={1}
          max={9999}
          value={stockNumbers[record._id] || defaultStockNumber}
          onChange={(value) => handleStockNumberChange(record._id, value)}
          disabled={!selectedItemIds.includes(record._id)}
          size='small'
          style={{ width: '100%' }}
        />
      ),
    },
  ]

  // 프로모션 페이지로 이동하는 함수
  const navigateToPromotionPage = () => {
    setShowModal(false)
    history.push('/summer-black-friday')
  }

  return (
    <>
      <Modal
        visible={showModal}
        onCancel={() => {
          if (!isSubmitting) {
            setShowModal(false)
            onClose && onClose()
          }
        }}
        width={singleItem ? 1000 : 1400}
        style={{ top: 10, maxHeight: '95vh' }}
        footer={null}
        closable={!isSubmitting}
        title={
          <div className='flex items-center justify-between pr-8'>
            <div className='flex items-center'>
              {/* 빗썸 로고 추가 */}

              <span className='text-lg font-bold'>
                올웨이즈 X 빗썸 여름 블랙프라이데이
                {singleItem ? ' 신청' : ' 일괄 신청'}
              </span>
            </div>
            <div className='flex items-center gap-4'>
              <a
                href='https://www.notion.so/FAQ-2290b952423c803fbb0edcb7ea229c38'
                target='_blank'
                rel='noopener noreferrer'
                className='flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-700 transition-colors duration-200'
                onClick={(e) => e.stopPropagation()}
              >
                <QuestionCircleOutlined className='text-base' />
                <span className='font-medium underline'>FAQ 보기</span>
              </a>
              <span className='text-xs font-normal text-gray-600'>
                {promotionInfo.period}
              </span>
            </div>
          </div>
        }
        bodyStyle={{
          padding: '12px 20px',
          maxHeight: 'calc(95vh - 55px)',
          overflowY: 'auto',
        }}
      >
        {isLoading ? (
          <div className='flex justify-center items-center h-64'>
            <Spin size='large' tip='상품 정보를 불러오는 중...' />
          </div>
        ) : (
          <div className='space-y-3'>
            {/* 프로모션 메인 안내 - 빗썸 주황색 스타일로 변경 */}
            <div className='relative bg-gradient-to-r from-[#FF6C00]/10 to-orange-50 rounded-lg p-3 pr-40 border border-[#FF6C00]/30 shadow-sm overflow-visible'>
              <div className='flex-1'>
                <h3 className='text-base font-bold text-gray-900 mb-1 flex items-center'>
                  <BankOutlined className='mr-2 text-[#FF6C00]' />
                  올웨이즈 X 빗썸 여름 블랙프라이데이
                  {/* FAQ 링크 추가 */}
                  <a
                    href='https://www.notion.so/FAQ-2290b952423c803fbb0edcb7ea229c38'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='ml-auto mr-36 inline-flex items-center gap-1 text-xs bg-amber-100 hover:bg-amber-200 text-amber-700 px-3 py-1 rounded-full transition-colors duration-200'
                    onClick={(e) => e.stopPropagation()}
                  >
                    <QuestionCircleOutlined />
                    <span className='font-medium'>
                      궁금한 점이 있으신가요? FAQ 보기
                    </span>
                  </a>
                </h3>
                <p className='text-xs text-gray-700 mb-2'>
                  <span className='font-semibold text-[#FF6C00]'>
                    빗썸과 함께하는 특별한 여름 프로모션!
                  </span>
                </p>
                <div className='flex gap-3'>
                  <div className='bg-white rounded px-3 py-1.5 shadow-sm'>
                    <div className='text-[10px] text-gray-600'>예상 매출</div>
                    <div className='text-lg font-bold text-[#FF6C00]'>
                      300% ↑
                    </div>
                  </div>
                  <div className='bg-white rounded px-3 py-1.5 shadow-sm'>
                    <div className='text-[10px] text-gray-600'>
                      프로모션 기간
                    </div>
                    <div className='text-xs font-bold text-gray-900'>
                      {promotionInfo.period}
                    </div>
                    <div className='text-[9px] text-gray-500 mt-0.5'>
                      ※ 일정은 운영 정책에 따라 조정될 수 있습니다.
                    </div>
                  </div>
                </div>
              </div>
              {/* 이미지를 박스 밖으로 배치 */}
              <div
                className='absolute -right-4 top-1/2 transform -translate-y-1/2 w-32 h-32 cursor-pointer hover:scale-105 transition-transform duration-200 group'
                onClick={() => setIsImagePopupOpen(true)}
              >
                <img
                  src={SummerBlackFridayPDP}
                  alt='여름 블랙프라이데이 PDP'
                  className='w-full h-full object-contain drop-shadow-lg'
                />
                <div className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center'>
                  <div className='opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-1.5 shadow-lg'>
                    <ZoomInOutlined className='text-lg text-gray-700' />
                  </div>
                </div>
              </div>
            </div>

            {/* 상단 정보 영역 - 컴팩트하게 */}
            <Row gutter={[12, 0]}>
              {/* 왼쪽: 프로모션 혜택 */}
              <Col span={12}>
                <Card
                  size='small'
                  className='h-full shadow-sm'
                  bodyStyle={{ padding: '10px 12px' }}
                >
                  <div className='flex items-center gap-2 mb-2'>
                    <div className='w-6 h-6 bg-red-100 rounded flex items-center justify-center'>
                      <GiftOutlined className='text-sm text-red-500' />
                    </div>
                    <span className='font-semibold text-sm'>프로모션 혜택</span>
                  </div>
                  <div className='space-y-1'>
                    {promotionInfo.benefits.map((benefit, index) => (
                      <div
                        key={index}
                        className='flex items-center gap-1.5 text-xs bg-gray-50 rounded px-2 py-1'
                      >
                        <CheckCircleOutlined className='text-green-500 text-[10px]' />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>

              {/* 오른쪽: 설정 영역 */}
              <Col span={12}>
                <Card
                  size='small'
                  className='h-full shadow-sm'
                  bodyStyle={{ padding: '10px 12px' }}
                >
                  <Row gutter={12}>
                    <Col span={12}>
                      <div className='space-y-1'>
                        <label className='text-xs font-medium text-gray-700'>
                          할인율 설정
                        </label>
                        <Select
                          value={discountRate}
                          onChange={setDiscountRate}
                          style={{ width: '100%' }}
                          size='small'
                        >
                          {discountOptions}
                        </Select>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className='space-y-1'>
                        <label className='text-xs font-medium text-gray-700'>
                          재고량 일괄 설정
                        </label>
                        <InputNumber
                          min={1}
                          max={99999999}
                          value={defaultStockNumber}
                          onChange={handleBulkStockNumberChange}
                          style={{ width: '100%' }}
                          size='small'
                        />
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            {/* 수수료 안내 - 간결하게 */}
            <div className='bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg px-3 py-2 border border-purple-200'>
              <div className='flex items-start gap-2'>
                <DollarOutlined className='text-purple-600 mt-0.5 text-sm' />
                <p className='text-xs text-gray-700 mb-0'>
                  프로모션 수수료{' '}
                  <span className='font-bold text-purple-600 text-sm'>
                    {promotionInfo.commissionRate}%
                  </span>
                  가 적용됩니다. (부가세 별도, PG 수수료 포함)
                  <span className='text-[11px] text-gray-600 ml-1'>
                    수수료는 쿠폰 지급, 추가 마케팅 지원 등 프로모션 활성화에
                    사용되어 더 많은 고객에게 노출되고 매출 증대 효과를
                    극대화합니다.
                  </span>
                </p>
              </div>
            </div>

            {/* 상품 목록 헤더 */}
            <div className='bg-gray-50 rounded-t-lg px-3 py-2 -mb-3'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <div className='w-6 h-6 bg-blue-100 rounded flex items-center justify-center'>
                    <ShoppingCartOutlined className='text-sm text-blue-600' />
                  </div>
                  <span className='font-semibold text-sm'>신청 대상 상품</span>
                  <div className='bg-white px-2 py-0.5 rounded-full shadow-sm'>
                    <span className='text-xs text-gray-600'>
                      전체{' '}
                      <span className='font-bold'>
                        {commaNumber(totalItemsCount)}
                      </span>
                      개 중{' '}
                      <span className='font-bold text-blue-600'>
                        {commaNumber(selectedItemIds.length)}
                      </span>
                      개 선택
                    </span>
                  </div>
                </div>
                <div className='text-[10px] text-gray-500'>
                  ✓ 개별 재고량 수정 가능
                </div>
              </div>
            </div>

            {/* 상품 목록 테이블 - 높이 축소 */}
            <div className='border border-gray-200 rounded-b-lg overflow-hidden'>
              <Table
                columns={columns}
                dataSource={displayItems}
                rowKey='_id'
                pagination={false}
                scroll={{ y: 240 }}
                virtual
                size='small'
                className='compact-modern-table'
                loading={isLoading}
              />
            </div>

            {/* 동의 및 액션 버튼 */}
            <div className='space-y-3'>
              {/* 안내 문구 - 더 컴팩트하게 */}
              <div className='bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-3 border border-amber-200'>
                <div className='flex items-start gap-2'>
                  <div className='w-6 h-6 bg-amber-100 rounded flex items-center justify-center flex-shrink-0'>
                    <CheckCircleOutlined className='text-amber-600 text-sm' />
                  </div>
                  <div className='text-xs text-gray-700'>
                    <div className='font-semibold text-sm mb-1'>
                      프로모션 참여 안내
                    </div>
                    <div className='grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px]'>
                      <div>
                        • 신청 상품:{' '}
                        <span className='font-bold text-red-600'>
                          {commaNumber(selectedItemIds.length)}개
                        </span>
                      </div>
                      <div>
                        • 수수료:{' '}
                        <span className='font-bold'>
                          {promotionInfo.commissionRate}%
                        </span>{' '}
                        (부가세 별도, PG 수수료 포함)
                      </div>
                      <div>
                        • 최소 할인율:{' '}
                        <span className='font-bold'>
                          {promotionInfo.minDiscountRate}%
                        </span>{' '}
                        이상
                      </div>
                      <div>
                        • 가격 정책: 온라인 최저가{' '}
                        <span className='font-bold'>+5% 이내</span>
                      </div>
                    </div>
                    <div className='mt-1.5 pt-1.5 border-t border-amber-200 text-[10px] text-gray-600'>
                      ※ 프로모션 일정은 운영 정책에 따라 조정될 수 있습니다.
                    </div>
                  </div>
                </div>
              </div>

              <div className='flex justify-between items-center'>
                <Button
                  size='middle'
                  onClick={() => {
                    if (isMainScreen) {
                      navigateToPromotionPage()
                    } else {
                      setShowModal(false)
                      onClose && onClose()
                    }
                  }}
                  disabled={isSubmitting}
                  className={isMainScreen ? 'min-w-[160px]' : ''}
                  style={
                    isMainScreen
                      ? {
                          background: '#374151',
                          borderColor: '#374151',
                          color: 'white',
                        }
                      : {}
                  }
                >
                  {isMainScreen ? (
                    <>
                      <ShoppingOutlined className='mr-1' />
                      프로모션 페이지 이동
                    </>
                  ) : (
                    '나중에 신청하기'
                  )}
                </Button>
                <Button
                  type='primary'
                  size='middle'
                  onClick={handleSubmit}
                  disabled={isSubmitting || selectedItemIds.length === 0}
                  loading={isSubmitting}
                  className='bg-gradient-to-r from-[#FF6C00] to-[#E55A00] border-none hover:from-[#E55A00] hover:to-[#CC4D00] shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all'
                  style={{ minWidth: 200 }}
                >
                  <ThunderboltOutlined className='mr-1' />
                  {singleItem
                    ? '프로모션 신청하기'
                    : selectedItemIds.length === totalItemsCount
                    ? '전체 상품 일괄 신청'
                    : `선택한 ${selectedItemIds.length}개 상품 신청`}
                </Button>
              </div>
            </div>
          </div>
        )}

        <style jsx>{`
          .compact-modern-table :global(.ant-table-thead > tr > th) {
            background: #f8f9fa !important;
            padding: 6px 10px !important;
            font-weight: 600 !important;
            font-size: 12px !important;
            border-bottom: 1px solid #e5e7eb !important;
          }
          .compact-modern-table :global(.ant-table-tbody > tr > td) {
            padding: 4px 10px !important;
            font-size: 12px !important;
          }
          .compact-modern-table :global(.ant-table-tbody > tr:hover > td) {
            background: #f3f4f6 !important;
          }
          .compact-modern-table :global(.ant-checkbox-wrapper) {
            margin-bottom: 0 !important;
          }
          .compact-modern-table :global(.ant-input-number-sm) {
            font-size: 12px !important;
          }
          .compact-modern-table :global(.ant-select-sm) {
            font-size: 12px !important;
          }
        `}</style>
      </Modal>

      {/* 이미지 팝업 모달은 기존과 동일 */}
      <ImageModal
        visible={isImagePopupOpen}
        onCancel={() => setIsImagePopupOpen(false)}
        footer={null}
        width='80%'
        centered
        bodyStyle={{ padding: 0 }}
        style={{ maxWidth: '900px' }}
      >
        <div className='flex items-center justify-center p-8 bg-gray-100'>
          <img
            src={SummerBlackFridayPDP}
            alt='여름 블랙프라이데이 PDP'
            className='w-full h-full object-contain'
            style={{ maxHeight: '80vh' }}
          />
        </div>
      </ImageModal>
    </>
  )
}

export default SummerBlackFridayBulkApplicationModal
